import type { PatientHubListRow } from '../types';

export type PatientMeasurementsFilter = 'all' | 'with' | 'without';

export type PatientListSort =
  | 'code-asc'
  | 'code-desc'
  | 'id-asc'
  | 'id-desc'
  | 'updated-desc';

export interface FilterPatientsListOptions {
  search?: string;
  measurementsFilter?: PatientMeasurementsFilter;
  sortBy?: PatientListSort;
}

const matchesSearch = (patient: PatientHubListRow, search: string) => {
  const normalizedSearch = search.trim().toLowerCase();
  if (normalizedSearch.length === 0) {
    return true;
  }

  const searchableValues = [
    patient.code,
    String(patient.id),
    patient.code.replace(/[^a-z0-9]/gi, ''),
  ];

  return searchableValues.some(value =>
    value.toLowerCase().includes(normalizedSearch)
  );
};

const matchesMeasurementsFilter = (
  patient: PatientHubListRow,
  measurementsFilter: PatientMeasurementsFilter
) => {
  if (measurementsFilter === 'all') {
    return true;
  }

  if (measurementsFilter === 'with') {
    return patient.hasBodyMeasurements;
  }

  return !patient.hasBodyMeasurements;
};

const getUpdatedAtTimestamp = (patient: PatientHubListRow) => {
  const value = patient.updatedAt ?? patient.createdAt;
  if (value == null) {
    return 0;
  }

  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const sortPatients = (
  patients: PatientHubListRow[],
  sortBy: PatientListSort
) => {
  const sorted = [...patients];

  sorted.sort((left, right) => {
    switch (sortBy) {
      case 'code-desc':
        return right.code.localeCompare(left.code, 'it');
      case 'id-asc':
        return left.id - right.id;
      case 'id-desc':
        return right.id - left.id;
      case 'updated-desc':
        return getUpdatedAtTimestamp(right) - getUpdatedAtTimestamp(left);
      case 'code-asc':
      default:
        return left.code.localeCompare(right.code, 'it');
    }
  });

  return sorted;
};

export const filterPatientsList = (
  patients: PatientHubListRow[],
  options: FilterPatientsListOptions = {}
): PatientHubListRow[] => {
  const search = options.search ?? '';
  const measurementsFilter = options.measurementsFilter ?? 'all';
  const sortBy = options.sortBy ?? 'code-asc';

  const filtered = patients.filter(
    patient =>
      matchesSearch(patient, search) &&
      matchesMeasurementsFilter(patient, measurementsFilter)
  );

  return sortPatients(filtered, sortBy);
};

export const hasActivePatientsListFilters = (
  options: FilterPatientsListOptions
) => {
  const search = options.search?.trim() ?? '';
  const measurementsFilter = options.measurementsFilter ?? 'all';
  const sortBy = options.sortBy ?? 'code-asc';

  return (
    search.length > 0 || measurementsFilter !== 'all' || sortBy !== 'code-asc'
  );
};
